// ===== K-Tec Requirement Parser =====
// Extracts structured requirements from user messages

import type { Language } from './languageDetector';

export interface ParsedRequirement {
  propertyType?: string;
  cameraQuantity?: number;
  budget?: number;
  nightVision?: boolean;
  fullColour?: boolean;
  outdoor?: boolean;
  indoor?: boolean;
  wifiCamera?: boolean;
  ipCamera?: boolean;
  analogCamera?: boolean;
  audioRequired?: boolean;
  twoWayAudio?: boolean;
  humanDetection?: boolean;
  vehicleDetection?: boolean;
  faceDetection?: boolean;
  aiFeatures?: boolean;
  storagedays?: number;
  floors?: number;
  internet?: boolean;
  remoteViewing?: boolean;
  preferredBrand?: string;
  serviceType?: string; // cctv, alarm, smart-home, starlink, networking
  areas?: string[];
}

// Property type keywords
const PROPERTY_KEYWORDS: Record<string, string[]> = {
  house: ['house', 'home', 'gedara', 'gedarata', 'geda', 'residence', 'villa', 'ගෙදර', 'නිවස'],
  shop: ['shop', 'store', 'boutique', 'kade', 'kadekata', 'kadeta', 'shops', 'කඩේ', 'කඩය'],
  office: ['office', 'kantora', 'kantorawa', 'offices', 'workplace', 'ඔෆිස්'],
  warehouse: ['warehouse', 'store room', 'godanaya', 'godanayata', 'storage', 'ගොදනය'],
  factory: ['factory', 'fabrika', 'plant', 'manufacturing', 'factory', 'කම්හල'],
  hotel: ['hotel', 'resort', 'guesthouse', 'lodge', 'hotelaya', 'හෝටල්'],
  apartment: ['apartment', 'flat', 'condo', 'aprt', 'flats', 'appartment', 'flat eka'],
  restaurant: ['restaurant', 'food place', 'cafe', 'cafeteria', 'kema', 'bhojanagara'],
  school: ['school', 'college', 'university', 'campus', 'paasal', 'iskola', 'ඉස්කෝලේ'],
  farm: ['farm', 'plantation', 'govi', 'govipola', 'estate'],
};

// Service type keywords
const SERVICE_KEYWORDS: Record<string, string[]> = {
  cctv: ['cctv', 'camera', 'cameras', 'surveillance', 'recording', 'ccam', 'කැමරා', 'camera eka', 'camera tikak'],
  alarm: ['alarm', 'alarms', 'burglar', 'intruder', 'sensor', 'alarm system', 'intrusion'],
  'smart-home': ['smart home', 'smart switch', 'automation', 'smart plug', 'smart light', 'alexa', 'google home'],
  starlink: ['starlink', 'internet', 'broadband', 'satellite internet', 'starlink eka'],
  networking: ['network', 'wifi', 'router', 'internet setup', 'lan', 'cat6', 'networking', 'wifi setup'],
  'access-control': ['access control', 'door lock', 'fingerprint', 'face recognition door', 'entrance'],
};

// Number word mappings
const NUMBER_WORDS: Record<string, number> = {
  'eka': 1, 'one': 1, 'ekak': 1, 'ekka': 1, 'ekai': 1,
  'deka': 2, 'two': 2, 'dekak': 2,
  'thuna': 3, 'three': 3, 'thunak': 3,
  'hathara': 4, 'four': 4, 'hatharak': 4,
  'pahak': 5, 'five': 5, 'paha': 5,
  'haya': 6, 'six': 6, 'hayak': 6,
  'hatha': 7, 'seven': 7, 'hathak': 7,
  'ata': 8, 'eight': 8, 'atak': 8,
  'nawa': 9, 'nine': 9, 'nawak': 9,
  'daha': 10, 'ten': 10, 'dahak': 10,
  'doloha': 12, 'twelve': 12,
  'soloha': 16, 'sixteen': 16,
};

export function parseRequirement(text: string, lang: Language): ParsedRequirement {
  const lower = text.toLowerCase();
  const req: ParsedRequirement = {};

  // Detect property type
  for (const [type, keywords] of Object.entries(PROPERTY_KEYWORDS)) {
    if (keywords.some(kw => lower.includes(kw))) {
      req.propertyType = type;
      break;
    }
  }

  // Detect service type
  for (const [type, keywords] of Object.entries(SERVICE_KEYWORDS)) {
    if (keywords.some(kw => lower.includes(kw))) {
      req.serviceType = type;
      break;
    }
  }

  // Detect camera quantity - look for numbers followed by camera keywords
  const cameraQuantityRegex = /(\d+)\s*(?:k|ck)?\s*(?:camera|cameras|cam|cams|කැමරා|යළිදු)/i;
  const numCameraMatch = lower.match(cameraQuantityRegex);
  if (numCameraMatch) {
    req.cameraQuantity = parseInt(numCameraMatch[1]);
  } else {
    // Look for number words
    // "camera 4k" style - where 4k can mean 4 cameras
    const ckMatch = lower.match(/camera[s]?\s+(\d+)k?\b/i) || lower.match(/(\d+)k?\s+camera/i);
    if (ckMatch) {
      req.cameraQuantity = parseInt(ckMatch[1]);
    } else {
      // Look for word numbers
      for (const [word, num] of Object.entries(NUMBER_WORDS)) {
        if (lower.includes(`camera ${word}`) || lower.includes(`${word} camera`) ||
            lower.includes(`${word}k camera`) || lower.includes(`camera ${word}k`)) {
          req.cameraQuantity = num;
          break;
        }
      }
      // Simple number extraction near camera context
      if (!req.cameraQuantity && lower.includes('camera')) {
        const simpleNum = lower.match(/\b(\d{1,2})\b/);
        if (simpleNum && parseInt(simpleNum[1]) <= 64) {
          req.cameraQuantity = parseInt(simpleNum[1]);
        }
      }
    }
  }

  // Budget detection
  const budgetRegex = /(?:budget|lkr|rs\.?|රු)\s*[:\-]?\s*([\d,]+(?:\s*k)?)/i;
  const budgetMatch = lower.match(budgetRegex);
  if (budgetMatch) {
    let budget = parseInt(budgetMatch[1].replace(/,/g, ''));
    if (budgetMatch[1].includes('k')) budget *= 1000;
    req.budget = budget;
  } else {
    // Look for standalone large numbers
    const numMatch = lower.match(/\b(\d{4,7})\b/);
    if (numMatch) {
      const n = parseInt(numMatch[1]);
      if (n >= 10000 && n <= 9999999) req.budget = n;
    }
  }

  // Night vision / full colour
  if (/(?:night|rathri|rathriya|rathre|dark|අඳුර|dark place|low light)/i.test(lower)) {
    req.nightVision = true;
  }
  if (/(?:full.?colour|color night|colour night|colorvu|full color|colour camera|rathri colour|night colour)/i.test(lower)) {
    req.fullColour = true;
    req.nightVision = true;
  }

  // WiFi vs IP vs Analog
  if (/(?:wifi|wi-fi|wireless|sim camera)/i.test(lower)) req.wifiCamera = true;
  if (/(?:\bip camera|ip cam|poe camera|network camera)/i.test(lower)) req.ipCamera = true;
  if (/(?:analog|analogue|hdcvi|hd camera)/i.test(lower)) req.analogCamera = true;

  // Audio
  if (/(?:audio|mic|microphone|sound|two.?way)/i.test(lower)) req.audioRequired = true;
  if (/(?:two.?way|speaker|talk)/i.test(lower)) req.twoWayAudio = true;

  // AI features
  if (/(?:human|person|people).?detect/i.test(lower)) req.humanDetection = true;
  if (/(?:vehicle|car|truck).?detect/i.test(lower)) req.vehicleDetection = true;
  if (/(?:face).?detect/i.test(lower)) req.faceDetection = true;
  if (/(?:ai|smart detect|intelligent|perimeter)/i.test(lower)) req.aiFeatures = true;

  // Outdoor/Indoor
  if (/(?:outdoor|outside|gate|garden|perimeter|parking|entrance|entrance area)/i.test(lower)) req.outdoor = true;
  if (/(?:indoor|inside|living room|bedroom|office interior)/i.test(lower)) req.indoor = true;

  // Brand preference
  if (/dahua/i.test(lower)) req.preferredBrand = 'Dahua';
  else if (/hikvision/i.test(lower)) req.preferredBrand = 'Hikvision';
  else if (/ezviz/i.test(lower)) req.preferredBrand = 'EZVIZ';
  else if (/imou/i.test(lower)) req.preferredBrand = 'IMOU';

  // Storage days
  const storageMatch = lower.match(/(\d+)\s*(?:day|days|දිනක්|dina)/i);
  if (storageMatch) req.storagedays = parseInt(storageMatch[1]);

  return req;
}

export function mergeRequirements(existing: ParsedRequirement, newReq: ParsedRequirement): ParsedRequirement {
  const merged = { ...existing };
  for (const [key, value] of Object.entries(newReq)) {
    if (value !== undefined) {
      (merged as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}

export function getRequirementSummary(req: ParsedRequirement): string {
  const parts: string[] = [];
  if (req.propertyType) parts.push(`Property: ${req.propertyType}`);
  if (req.serviceType) parts.push(`Service: ${req.serviceType}`);
  if (req.cameraQuantity) parts.push(`Cameras: ${req.cameraQuantity}`);
  if (req.fullColour) parts.push('Full Colour Night Vision');
  else if (req.nightVision) parts.push('Night Vision');
  if (req.wifiCamera) parts.push('Wi-Fi Camera');
  if (req.budget) parts.push(`Budget: LKR ${req.budget.toLocaleString()}`);
  return parts.join(' | ');
}
